/**
 * Compresses an image file to a target size
 * @param {File|Blob} file - The image file to compress
 * @param {number} maxSizeKB - Maximum size in KB (default: 500KB)
 * @param {number} quality - Initial quality (0-1, default: 0.8)
 * @returns {Promise<File>} - Compressed image file
 */
export const compressImage = async (file, maxSizeKB = 500, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    // If file is already small enough, return it
    if (file.size <= maxSizeKB * 1024) {
      resolve(file);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        // Create canvas
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions while maintaining aspect ratio
        // Max dimensions for profile photo
        const MAX_WIDTH = 800;
        const MAX_HEIGHT = 800;

        if (width > height) {
          if (width > MAX_WIDTH) {
            height = Math.round((height * MAX_WIDTH) / width);
            width = MAX_WIDTH;
          }
        } else {
          if (height > MAX_HEIGHT) {
            width = Math.round((width * MAX_HEIGHT) / height);
            height = MAX_HEIGHT;
          }
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Try compressing with decreasing quality until size is acceptable
        const tryCompress = (currentQuality) => {
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Failed to compress image'));
                return;
              }

              // If still too large and quality can be reduced further
              if (blob.size > maxSizeKB * 1024 && currentQuality > 0.1) {
                tryCompress(currentQuality - 0.1);
              } else {
                // Create file from blob
                const compressedFile = new File([blob], file.name || 'compressed-image.jpg', {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });
                resolve(compressedFile);
              }
            },
            'image/jpeg',
            currentQuality
          );
        };

        tryCompress(quality);
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

/**
 * Compresses a base64 image string
 * @param {string} base64String - The base64 image string
 * @param {number} maxSizeKB - Maximum size in KB (default: 500KB)
 * @param {number} quality - Initial quality (0-1, default: 0.8)
 * @returns {Promise<{base64: string, blob: Blob}>} - Compressed base64 and blob
 */
export const compressBase64Image = async (base64String, maxSizeKB = 500, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;

      const MAX_WIDTH = 800;
      const MAX_HEIGHT = 800;

      if (width > height) {
        if (width > MAX_WIDTH) {
          height = Math.round((height * MAX_WIDTH) / width);
          width = MAX_WIDTH;
        }
      } else {
        if (height > MAX_HEIGHT) {
          width = Math.round((width * MAX_HEIGHT) / height);
          height = MAX_HEIGHT;
        }
      }

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      const tryCompress = (currentQuality) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to compress image'));
              return;
            }

            if (blob.size > maxSizeKB * 1024 && currentQuality > 0.1) {
              tryCompress(currentQuality - 0.1);
            } else {
              const reader = new FileReader();
              reader.onload = () => {
                resolve({
                  base64: reader.result,
                  blob: blob
                });
              };
              reader.readAsDataURL(blob);
            }
          },
          'image/jpeg',
          currentQuality
        );
      };

      tryCompress(quality);
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = base64String;
  });
};
